import { type Colors, type ColorVariants } from '@/styles/styles.types';
import { cn, makeTailwindTextColor } from '@/styles/styles.utils';
import { cva, type VariantProps } from 'class-variance-authority';
import { type PropsWithChildren } from 'react';

import type { TypographyElements } from './typography.types';

/* TODO:
     - Typography (everything as props)
        - Modifiers
          - Span - color
        - Links?
 */

const typographyStyles = cva('', {
  variants: {
    weight: {
      100: 'font-thin',
      200: 'font-extralight',
      300: 'font-light',
      400: 'font-normal',
      500: 'font-medium',
      600: 'font-semibold',
      700: 'font-bold',
      800: 'font-extrabold',
      900: 'font-black',
    },
    italic: {
      true: 'italic',
      false: 'not-italic',
    },
    line: {
      under: 'underline',
      over: 'overline',
      through: 'line-through',
      none: 'no-underline',
    },
    alignX: {
      left: 'text-left',
      center: 'text-center',
      right: 'text-right',
    },
    alignY: {
      top: 'align-top',
      middle: 'align-middle',
      bottom: 'align-bottom',
    },
  },
  defaultVariants: {
    weight: 400,
    italic: false,
    line: 'none',
    alignX: 'left',
    alignY: 'top',
  },
});

type Props = VariantProps<typeof typographyStyles> &
  PropsWithChildren<{
    element: TypographyElements;
    classNames: string;
    color?: Colors;
    colorVariant?: ColorVariants;
  }>;

const Typography = ({ element, classNames, children, weight, italic, line, alignX, alignY, colorVariant, color = 'current' }: Props) => {
  const Tag = element;
  const textColor = makeTailwindTextColor(color, colorVariant);
  return (
    <Tag
      className={cn(
        typographyStyles({
          weight,
          italic,
          line,
          alignX,
          alignY,
        }),
        textColor,
        classNames
      )}
    >
      {children}
    </Tag>
  );
};

export default Typography;
